// ============================================================
// VinylEstimator.js
// Single-screen Vinyl (PVC) Privacy estimator.
//
// Mirrors Estimator.js's navigation contract (returnTo / previousSpecs /
// priceLevel via location.state) but is a flat one-page form instead of a
// wizard. On "Calculate", it runs calculateVinyl and navigates back to the
// originating document with { lineItems, estimatorSpecs, priceLevel } — the
// same shape NewDocument/EditDocument already ingest generically.
//
// Product: PVC Full Privacy — one style, 6' tall, 8'-wide panels.
// ============================================================

import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { calculateVinyl } from '../utils/vinylCalc';

let runIdCounter = 1;
const newRun = () => ({ id: runIdCounter++, length: '' });

export default function VinylEstimator({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useData();
  const returnTo = location?.state?.returnTo || null;
  const previousSpecs = location?.state?.previousSpecs || null;
  const docPriceLevel = location?.state?.priceLevel || 'A';
  const userName = user?.email?.split('@')[0]?.toUpperCase();

  // ── State (seeded from previousSpecs so "modify" pre-fills) ──
  const [runs, setRuns] = useState(() => {
    if (previousSpecs?.runs?.length > 0) {
      // Reassign IDs to avoid collisions with the module-level counter.
      return previousSpecs.runs.map(r => ({ id: runIdCounter++, length: r.length ?? '' }));
    }
    return [newRun()];
  });
  const [totalCorners, setTotalCorners] = useState(() => previousSpecs?.totalCorners ?? 0);
  const [totalEnds, setTotalEnds] = useState(() => previousSpecs?.totalEnds ?? 0);
  const [capType, setCapType] = useState(() => previousSpecs?.capType || 'horse');
  const [latchType, setLatchType] = useState(() => previousSpecs?.latchType || 'paddle');
  const [gates, setGates] = useState(() => previousSpecs?.gates || []);

  const runInputRefs = useRef({});

  // ── Run helpers (copied from Estimator.js) ──
  const updateRun = (id, length) => setRuns(prev => prev.map(r => r.id === id ? { ...r, length } : r));
  const removeRun = (id) => { if (runs.length > 1) setRuns(prev => prev.filter(r => r.id !== id)); };
  const addRun = () => {
    const nr = newRun();
    setRuns(prev => [...prev, nr]);
    setTimeout(() => runInputRefs.current[nr.id]?.focus(), 50);
  };
  const handleRunKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRun(); } };

  // ── Gate helpers (simple inline list — no modal) ──
  const addGate = () => setGates(prev => [...prev, { width: '4', kind: 'single' }]);
  const updateGate = (idx, field, value) => setGates(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  const removeGate = (idx) => setGates(prev => prev.filter((_, i) => i !== idx));

  // ── Specs + calculate ──
  const buildSpecs = () => ({
    kind: 'vinyl', // discriminator so the correct "Modify" route can be chosen later
    runs,
    totalCorners: parseInt(totalCorners) || 0,
    totalEnds: parseInt(totalEnds) || 0,
    capType,
    latchType,
    gates,
  });

  const handleCalculate = () => {
    if (returnTo && (!products || products.length === 0)) {
      alert('Products are still loading. Please wait a moment and try again.');
      return;
    }
    const specs = buildSpecs();
    if (returnTo) {
      const { items } = calculateVinyl(specs, products, docPriceLevel);
      navigate(returnTo, { state: { lineItems: items, estimatorSpecs: specs, priceLevel: docPriceLevel } });
    } else {
      // No standalone results page for vinyl yet — go back to dashboard.
      navigate('/dashboard');
    }
  };

  // ── Derived ──
  const totalFootage = runs.reduce((s, r) => s + (parseFloat(r.length) || 0), 0);
  const canCalc = runs.some(r => parseFloat(r.length) > 0);

  // ── Shared inline styles ──
  const cardStyle = { background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 };
  const sectionTitle = { color: '#03045E', fontWeight: 700, margin: '0 0 12px 0' };
  const labelStyle = { color: '#6B7C93', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' };
  const inputStyle = {
    width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px',
    fontSize: 16, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s',
  };
  const selectStyle = { ...inputStyle, fontSize: 14 };
  const focusOn = (e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; };
  const focusOff = (e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; };

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* 1. Header */}
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>Vinyl (PVC) Estimator</h1>
              <p style={{ color: '#6B7C93', fontSize: 13, margin: '2px 0 0 0' }}>Full Privacy · 6' · 8' panels{userName ? ` · ${userName}` : ''}</p>
            </div>
          </div>
        </div>

        {/* 2. Fence Layout */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Fence Layout</p>

          <div className="space-y-2" style={{ marginBottom: 8 }}>
            {runs.map((run, idx) => (
              <div key={run.id} className="flex items-center gap-2">
                <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Run {idx + 1}</span>
                <input
                  type="number"
                  value={run.length === 0 ? '' : run.length}
                  ref={el => { runInputRefs.current[run.id] = el; }}
                  onChange={e => updateRun(run.id, e.target.value)}
                  onKeyDown={handleRunKeyDown}
                  onFocus={e => { e.target.select(); focusOn(e); }}
                  onBlur={focusOff}
                  style={{ ...inputStyle, flex: 1, fontSize: 18 }}
                  placeholder="ft"
                  min="1"
                />
                <span style={{ color: '#6B7C93', fontSize: 12 }}>ft</span>
                {runs.length > 1 && (
                  <button onClick={() => removeRun(run.id)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontWeight: 700, padding: '0 4px', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addRun} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Run
          </button>
          {totalFootage > 0 && (
            <div style={{ marginTop: 12, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: 12 }}>
              <p style={{ color: '#03045E', fontWeight: 700, margin: 0 }}>Total: {totalFootage.toLocaleString()} ft</p>
              <p style={{ color: '#0077B6', fontSize: 12, margin: '2px 0 0 0' }}>{runs.filter(r => parseFloat(r.length) > 0).length} run(s)</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3" style={{ marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Corner Posts</label>
              <input
                type="number" min="0"
                value={totalCorners === 0 ? '' : totalCorners}
                onChange={e => setTotalCorners(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
            <div>
              <label style={labelStyle}>End Posts (includes gate posts)</label>
              <input
                type="number" min="0"
                value={totalEnds === 0 ? '' : totalEnds}
                onChange={e => setTotalEnds(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* 3. Options */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Options</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Post Cap</label>
              <select value={capType} onChange={e => setCapType(e.target.value)} onFocus={focusOn} onBlur={focusOff} style={selectStyle}>
                <option value="horse">Horse Cap</option>
                <option value="frenchGothic">French Gothic</option>
                <option value="newEngland">New England</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Latch</label>
              <select value={latchType} onChange={e => setLatchType(e.target.value)} onFocus={focusOn} onBlur={focusOff} style={selectStyle}>
                <option value="paddle">Paddle</option>
                <option value="topPull">Top Pull</option>
              </select>
            </div>
          </div>
        </div>

        {/* 4. Gates */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Gates</p>
          {gates.length === 0 ? (
            <p style={{ color: '#6B7C93', fontSize: 13, margin: '0 0 8px 0' }}>No gates</p>
          ) : (
            <div className="space-y-2" style={{ marginBottom: 8 }}>
              {gates.map((gate, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Gate {i + 1}</span>
                  <select value={gate.width} onChange={e => updateGate(i, 'width', e.target.value)} onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1 }}>
                    <option value="4">4'</option>
                    <option value="5">5'</option>
                  </select>
                  <select value={gate.kind} onChange={e => updateGate(i, 'kind', e.target.value)} onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1 }}>
                    <option value="single">Single</option>
                    <option value="double">Double Pair</option>
                  </select>
                  <button onClick={() => removeGate(i)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <button onClick={addGate} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Gate
          </button>
        </div>

        {/* 5. Footer */}
        <div className="flex justify-between" style={{ marginTop: 4 }}>
          <button
            onClick={() => navigate(returnTo || '/dashboard')}
            style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleCalculate}
            disabled={!canCalc}
            style={{
              background: canCalc ? '#0077B6' : '#E3ECF5',
              border: 'none', color: canCalc ? '#FFFFFF' : '#6B7C93',
              fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15,
              cursor: canCalc ? 'pointer' : 'default',
              boxShadow: canCalc ? '0 4px 14px rgba(0,119,182,0.3)' : 'none',
            }}>
            Calculate &amp; Add to Document
          </button>
        </div>

      </div>
    </div>
  );
}
